import React, { useEffect, useMemo, useRef } from 'react';
import Markdown from '../common/Markdown';
import ChatProposalCard from './ChatProposalCard';

function normalizeRole(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'assistant' || r === 'user' || r === 'system') return r;
  return 'assistant';
}

function stripStreamingArtifacts(text) {
  let s = String(text || '');

  // Remove any complete control tags if they leak through.
  s = s.replace(/<\/?reply_markdown[^>]*>/g, '');
  s = s.replace(/<\/?json[^>]*>/g, '');

  // Remove any trailing incomplete tag fragment (e.g. "</reply_markd")
  // by trimming from the last '<' if it isn't closed by a '>' after it.
  const lastLt = s.lastIndexOf('<');
  if (lastLt >= 0) {
    const lastGt = s.lastIndexOf('>');
    if (lastGt < lastLt) {
      s = s.slice(0, lastLt);
    }
  }

  return s;
}

function TypingDots() {
  return (
    <div className="text-sm text-gray-600 leading-relaxed">
      <span className="inline-flex gap-1">
        <span className="animate-pulse">•</span>
        <span className="animate-pulse [animation-delay:150ms]">•</span>
        <span className="animate-pulse [animation-delay:300ms]">•</span>
      </span>
    </div>
  );
}

export default function ChatMessageList({ messages, onApplyProposal, onIgnoreProposal, plan = null }) {
  const listRef = useRef(null);

  const items = useMemo(() => (Array.isArray(messages) ? messages : []).map((m) => ({
    id: m?.id || `${m?.role}-${m?.created_at}-${Math.random()}`,
    role: normalizeRole(m?.role),
    content: String(m?.content || ''),
    created_at: m?.created_at || null,
    proposal: m?.meta_json?.proposal || null,
    proposal_action: m?.meta_json?.proposal_action || null,
    streaming: Boolean(m?.meta_json?.streaming)
  })), [messages]);

  const scrollKey = useMemo(() => {
    const last = items[items.length - 1] || null;
    if (!last) return 'empty';
    return `${String(last.id)}:${String(last.role)}:${String(last.content || '').length}`;
  }, [items]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    // Keep pinned to bottom while the assistant message is streaming/expanding.
    // Use rAF to let layout settle after React renders.
    requestAnimationFrame(() => {
      try {
        el.scrollTop = el.scrollHeight;
      } catch (e) {
        // ignore
      }
    });
  }, [scrollKey]);

  return (
    <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-3 md:p-4 bg-white">
      <div className="space-y-3">
        {items.map((m) => {
          const isUser = m.role === 'user';
          const safeAssistantContent = m.streaming ? stripStreamingArtifacts(m.content) : m.content;
          return (
            <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={[
                'max-w-[92%] md:max-w-[80%] rounded-2xl px-4 py-3 border',
                isUser ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-gray-50 text-gray-900 border-gray-200'
              ].join(' ')}>
                {isUser ? (
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
                ) : (
                  (m.streaming && !safeAssistantContent.trim()) ? (
                    <TypingDots />
                  ) : (
                    <Markdown className="text-sm text-gray-900 leading-relaxed [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1">
                      {safeAssistantContent}
                    </Markdown>
                  )
                )}

                {!isUser ? (
                  <ChatProposalCard
                    messageId={m.id}
                    proposal={m.proposal}
                    proposalAction={m.proposal_action}
                    onApply={onApplyProposal}
                    onIgnore={onIgnoreProposal}
                    plan={plan}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


